import { useEffect, useMemo, useRef } from "react"
import { ConfigQuery, fetchDimensionDetailsForUser } from "../../../createOrderApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { DynamicAccountingKey } from "../../form/formTypes"
import { extractValue } from "./helpers"

type DimensionGuardProps = Readonly<{
    userId: number
    fieldKey: DynamicAccountingKey
    value: unknown
    setFieldValue: (field: string, value: unknown) => void
    dimensionIdFromConfig?: number
}>

export function DimensionGuard({ userId, fieldKey, value, setFieldValue, dimensionIdFromConfig }: DimensionGuardProps) {
    const parsedIdFromKey = Number(fieldKey.split("_")[2])
    const initialDimensionId = dimensionIdFromConfig ?? (Number.isFinite(parsedIdFromKey) ? parsedIdFromKey : null)

    const frozenDimensionIdRef = useRef<number | null>(null)
    if (frozenDimensionIdRef.current === null && initialDimensionId != null) {
        frozenDimensionIdRef.current = initialDimensionId
    }
    const frozenDimensionId = frozenDimensionIdRef.current

    const startedRef = useRef(false)
    const fetchedOnceRef = useRef(false)

    const enabled = useMemo(() => {
        return frozenDimensionId !== null && !fetchedOnceRef.current && !startedRef.current
    }, [frozenDimensionId])

    useEffect(() => {
        if (enabled) startedRef.current = true
    }, [enabled])

    const { data, isLoading, isError } = useApiQuery(fetchDimensionDetailsForUser, {
        queryName: ConfigQuery.ACCOUNT_DIMENSION_DETAILS_FOR_USER,
        dependencies: {
            userId,
            dimensionId: frozenDimensionId ?? 0,
            search: "",
        },
        enabled,
    })

    useEffect(() => {
        if (!startedRef.current || isLoading || fetchedOnceRef.current) return
        fetchedOnceRef.current = true

        const list = data?.accountDimension?.costCenters ?? []

        if (list.length === 0) return

        const formValue = extractValue(value)
        const exists = formValue ? list.some((opt) => String(opt.value) === formValue) : false

        if (isError || !exists) {
            setFieldValue(fieldKey, null)
        }
    }, [isLoading, isError, data, value, fieldKey, setFieldValue])

    return null
}
